import { useState } from 'react';
import { Phone, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { axiosInstance } from '../../App';

export default function PhoneValidation({ value, onChange, onValidationChange, disabled = false, skipValidation = false }) {
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [availablePhones, setAvailablePhones] = useState([]);
  const [showPhoneList, setShowPhoneList] = useState(false);

  const validatePhone = async (phone) => {
    // Module Invoiless OFF: langkau semakan Invoiless sepenuhnya
    if (skipValidation) {
      setValidationResult(null);
      setShowPhoneList(false);
      if (onValidationChange) onValidationChange(true, null);
      return;
    }
    if (!phone || phone.length < 10) {
      setValidationResult(null);
      setShowPhoneList(false);
      if (onValidationChange) onValidationChange(false, null);
      return;
    }

    setValidating(true);
    try {
      // Validate phone with Invoiless
      const response = await axiosInstance.get(`/parent/validate-phone/${phone}`);
      
      const result = {
        valid: response.data.valid,
        message: response.data.message,
        canProceed: response.data.can_proceed !== false, // Default to true if not specified
        warning: response.data.warning || null
      };
      
      setValidationResult(result);
      
      if (response.data.valid) {
        setShowPhoneList(false);
        if (onValidationChange) onValidationChange(true, null);
      } else {
        // Phone not found in Invoiless - but can still proceed with warning
        if (response.data.can_proceed) {
          // Allow proceed but pass warning
          if (onValidationChange) onValidationChange(true, response.data.warning);
        } else {
          // Don't allow proceed
          if (onValidationChange) onValidationChange(false, null);
        }
        
        // Try to fetch available phones for suggestions
        try {
          const availableResponse = await axiosInstance.get('/parent/available-invoiless-phones');
          setAvailablePhones(availableResponse.data.phones || []);
          setShowPhoneList((availableResponse.data.phones || []).length > 0);
        } catch {
          setAvailablePhones([]);
          setShowPhoneList(false);
        }
      }
    } catch (error) {
      console.error('Validation error:', error);
      // On error, allow proceed (can't verify)
      setValidationResult({
        valid: true,
        message: "Pengesahan tidak dapat dilakukan",
        canProceed: true,
        warning: null
      });
      setShowPhoneList(false);
      if (onValidationChange) onValidationChange(true, null);
    } finally {
      setValidating(false);
    }
  };

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    onChange(newPhone);
    
    // Clear previous result when typing
    setValidationResult(null);
    
    // Debounce validation
    if (window.phoneValidationTimeout) {
      clearTimeout(window.phoneValidationTimeout);
    }
    window.phoneValidationTimeout = setTimeout(() => {
      validatePhone(newPhone);
    }, 1000);
  };

  const selectPhone = (phone) => {
    onChange(phone);
    setShowPhoneList(false);
    validatePhone(phone);
  };

  // Determine border and icon color based on validation state
  const getBorderClass = () => {
    if (!validationResult) return 'border-gray-300';
    if (validationResult.valid) return 'border-green-500';
    if (validationResult.canProceed) return 'border-orange-400'; // Warning state
    return 'border-red-500';
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          type="tel"
          value={value}
          onChange={handlePhoneChange}
          disabled={disabled}
          placeholder="01XXXXXXXX"
          data-testid="phone-input"
          className={`w-full px-3 py-2 border-2 rounded-xl focus:ring-2 focus:ring-pink-200 transition-all outline-none ${getBorderClass()}`}
        />
        {validating && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-500"></div>
          </div>
        )}
        {!validating && validationResult?.valid === true && (
          <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" />
        )}
        {!validating && validationResult?.valid === false && validationResult?.canProceed && (
          <AlertTriangle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-500" />
        )}
        {!validating && validationResult?.valid === false && !validationResult?.canProceed && (
          <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-600" />
        )}
      </div>

      {validationResult && (
        <div className={`text-sm flex items-start gap-2 p-3 rounded-xl ${
          validationResult.valid 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : validationResult.canProceed
            ? 'bg-orange-50 text-orange-800 border border-orange-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {validationResult.valid ? (
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          ) : validationResult.canProceed ? (
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1">
            <p className="font-medium">{validationResult.message}</p>
            {validationResult.warning && (
              <p className="text-xs mt-1 font-normal">
                ⚠️ {validationResult.warning}
              </p>
            )}
            {!validationResult.valid && !validationResult.warning && validationResult.canProceed && (
              <p className="text-xs mt-1 font-normal">
                ⚠️ Anda masih boleh simpan nombor ini, tetapi status invois mungkin tidak dapat dipaparkan.
              </p>
            )}
          </div>
        </div>
      )}

      {showPhoneList && availablePhones.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3">
          <p className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Nombor telefon yang tersedia dari Invoiless:
          </p>
          <div className="space-y-2">
            {availablePhones.map((phoneData, index) => (
              <button
                key={index}
                onClick={() => selectPhone(phoneData.phone)}
                className="w-full text-left px-3 py-2 bg-white hover:bg-blue-100 rounded-lg border-2 border-blue-300 transition-colors"
                data-testid={`phone-suggestion-${index}`}
              >
                <p className="font-mono text-sm font-bold">{phoneData.phone}</p>
                {phoneData.name && (
                  <p className="text-xs text-gray-600">{phoneData.name}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
